import React, { useMemo } from "react";
import {
  Box,
  Card,
  Chip,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { useSelector } from "react-redux";

// Tooltip copy — kept verbose so operators understand the metric, the formula,
// and what "good" looks like. ASA tooltip explicitly notes it's not PDD so the
// user knows the caveat.
const METRIC_INFO = {
  totalCalls: {
    title: "Total Calls (today)",
    description:
      "All call attempts routed through DIDs assigned to this carrier today, excluding suspended-DID hits. Answered + missed + failed combined.",
  },
  asr: {
    title: "ASR (Answer-Seizure Ratio)",
    description:
      "Percentage of today's call attempts that were actually answered (had talk time). Formula: answered_calls / total_calls × 100. Low ASR points to bad routing, dead numbers, or a carrier delivery issue.",
    thresholds: "Green: >70% · Yellow: 50-70% · Red: <50%",
  },
  acd: {
    title: "ACD (Average Call Duration)",
    description:
      "Mean talk-time across answered calls today, in seconds. Short ACD with low ASR usually signals call-quality or routing problems — callers connect but hang up fast.",
    thresholds: "Green: >180s · Yellow: 120-180s · Red: <120s",
  },
  asa: {
    title: "ASA (Average Speed of Answer)",
    description:
      "Mean seconds between dial (start_at) and connect (answer_at) across answered calls today. Lower is better — long ASA indicates slow carrier routing or buyers picking up late. Note: this is ASA, not PDD — true Post-Dial-Delay needs SIP 18x progress timing which isn't captured today.",
    thresholds: "Green: <8s · Yellow: 8-15s · Red: >15s",
  },
};

const getAsrColor = (asr) => {
  if (asr > 70) return "#2e7d32";
  if (asr >= 50) return "#f59e0b";
  return "#dc2626";
};
const getAcdColor = (acd) => {
  if (acd > 180) return "#2e7d32";
  if (acd >= 120) return "#f59e0b";
  return "#dc2626";
};
const getAsaColor = (asa, hasAnswers) => {
  if (!hasAnswers) return "#9e9e9e";
  if (asa < 8) return "#2e7d32";
  if (asa <= 15) return "#f59e0b";
  return "#dc2626";
};

const RANK_COLORS = ["#0a140e", "#0a140e", "#0a140e", "#0a140e"];

// Compact info icon next to a metric label. Hover shows title + description
// + threshold band. Mirrors the InfoIcon already used in CarrierMetricsChart.
function MetricInfoIcon({ metricKey }) {
  const info = METRIC_INFO[metricKey];
  if (!info) return null;
  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#fff" }}>
            {info.title}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, color: "#e0e0e0" }}>
            {info.description}
          </Typography>
          {info.thresholds && (
            <Typography
              variant="caption"
              sx={{ mt: 0.5, display: "block", color: "#b0bec5" }}
            >
              {info.thresholds}
            </Typography>
          )}
        </Box>
      }
      arrow
      placement="top"
    >
      <InfoOutlinedIcon sx={{ fontSize: 13, color: "#9e9e9e", cursor: "help", ml: 0.3 }} />
    </Tooltip>
  );
}

// One pill = one metric for one carrier. Color encodes good/bad per the
// thresholds above; tooltip explains the formula and bands.
function MetricPill({ label, value, color, metricKey }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.4,
        px: 1.1,
        py: 0.4,
        borderRadius: "10px",
        background: `${color}14`,
        border: `1px solid ${color}33`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.7rem",
          fontWeight: 700,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 700,
          color: `${color} !important`,
        }}
      >
        {value}
      </Typography>
      <MetricInfoIcon metricKey={metricKey} />
    </Box>
  );
}

function TopCarriersWidget() {
  // Read from the same Redux store CarrierMetricsChart populates — saves one
  // round-trip. CarrierMetricsChart dispatches getCarrierMetrics on mount, so
  // by the time this widget renders the data is on the wire.
  const { loading, data } = useSelector((state) => state.carrierMetrics);

  const top4 = useMemo(() => {
    if (!data?.data) return [];
    const today = new Date().toISOString().split("T")[0];
    return data.data
      .filter(
        (d) =>
          d.date === today &&
          d.carrier &&
          d.carrier !== "Unknown" &&
          d.total_calls > 0
      )
      .sort((a, b) => b.total_calls - a.total_calls)
      .slice(0, 4);
  }, [data]);

  return (
    <Box
      sx={{
        // Stretch to fill the parent flex slot in AdminDashboard so the
        // widget aligns vertically with ServerStatsCombined regardless of
        // how many carrier rows are rendered.
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
      }}
    >
      {/* Header chip — visually matches Server Monitoring's header for symmetry */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Chip
          icon={<LeaderboardIcon style={{ color: "#fff" }} />}
          label="Top 4 Carriers · Today"
          sx={{
            px: 1.2,
            py: 1,
            fontWeight: 600,
            background:
              "linear-gradient(135deg, #0a140e 0%, #0a140e 100%)",
            color: "#fff",
            fontSize: "0.85rem",
            letterSpacing: 0.5,
            "& .MuiChip-icon": { color: "#fff" },
          }}
        />
        <Tooltip
          title={
            <Box sx={{ p: 0.5, maxWidth: 300 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: "#fff" }}>
                How carriers are ranked
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, color: "#e0e0e0" }}>
                The carriers with the highest total call attempts today, joined
                via <code>rc_redirect_did.carrier_name</code>. DIDs without a
                configured carrier ("Unknown") are excluded so the ranking
                reflects real carrier traffic only.
              </Typography>
            </Box>
          }
          arrow
          placement="top"
        >
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9e9e9e", cursor: "help" }} />
        </Tooltip>
      </Box>

      <Card
        sx={{
          p: 1.5,
          borderRadius: "20px",
          background: "#ffffffcc",
          backdropFilter: "blur(10px)",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.08)",
          minHeight: 340,
          flex: 1,
        }}
      >
        {loading && (
          <Stack spacing={0.8}>
            {[0, 1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={56}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Stack>
        )}

        {!loading && top4.length === 0 && (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <LeaderboardIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "#212121 !important" }}>
              No carrier traffic today
            </Typography>
            <Typography sx={{ fontSize: "0.8rem", color: "#64748b" }}>
              Cards populate once calls land on DIDs with a configured carrier.
            </Typography>
          </Box>
        )}

        {!loading && top4.length > 0 && (
          <Stack spacing={0.8}>
            {top4.map((c, idx) => {
              const asrColor = getAsrColor(c.asr);
              const acdColor = getAcdColor(c.acd);
              const asaColor = getAsaColor(c.asa, c.answered_calls > 0);
              const rankColor = RANK_COLORS[idx] || "#0a140e";
              return (
                <Box
                  key={`${c.carrier}-${idx}`}
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    background: "#f8fafc",
                    border: "1px solid #e5e7eb",
                    transition: "0.2s",
                    "&:hover": { background: "#f1f5f9", transform: "translateY(-1px)" },
                  }}
                >
                  {/* Header: rank + carrier name + total calls */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 26,
                        height: 26,
                        borderRadius: "50%",
                        background: rankColor,
                        color: "#fff !important",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: "0.78rem",
                        flexShrink: 0,
                      }}
                    >
                      {idx + 1}
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "0.95rem",
                        fontWeight: 700,
                        color: "#0a140e !important",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={c.carrier}
                    >
                      {c.carrier}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                      <Typography
                        sx={{
                          fontSize: "1.05rem",
                          fontWeight: 800,
                          color: "#1976d2 !important",
                          lineHeight: 1,
                        }}
                      >
                        {c.total_calls}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#64748b !important",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.4,
                        }}
                      >
                        calls
                      </Typography>
                      <MetricInfoIcon metricKey="totalCalls" />
                    </Box>
                  </Box>

                  {/* Metric pills */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.8 }}>
                    <MetricPill
                      label="ASR"
                      value={`${c.asr}%`}
                      color={asrColor}
                      metricKey="asr"
                    />
                    <MetricPill
                      label="ACD"
                      value={`${c.acd}s`}
                      color={acdColor}
                      metricKey="acd"
                    />
                    <MetricPill
                      label="ASA"
                      value={`${c.asa}s`}
                      color={asaColor}
                      metricKey="asa"
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Card>
    </Box>
  );
}

export default TopCarriersWidget;
